import type { PageTargeting } from "../config/page";
import type { SlotConfigMap } from "../config/platforms/types";
import type { AdNodeManager } from "./AdNodeManager";
import type { GptWrapper } from "./GptWrapper";
import type { AdNode } from "./AdNode";
import type { FandomCmp } from "common-types";
import type { InstantConfigRepository } from "instant-config";
import type { AdEngine } from "./AdEngine";

/**
 * Auction specific information that is passed to all the bidders.
 */
export type AuctionData = {
  /**
   * Unique identifier for the auction. This is passed to all the bidders and can be used to
   * associated tracking across different bidders and events. This is a uuid string.
   *
   * Example: `f3b8c1e0-4d2a-4f5b-8c7e-9d6f1a2e3b4c`
   */
  auctionId: string;
  /**
   * The list of ad nodes that are being rendered in this auction.
   */
  adNodes: AdNode[];
  /**
   * The current page targeting object taken from the GptWrapper. This is passed to all the bidders
   * so they don't have to keep requesting it from the GptWrapper.
   */
  pageTargeting: PageTargeting;
  /**
   * The render cycle count for the AdEngine. The first render cycle is 1 and increments by 1.
   * This can be used for tracking, debugging, and differentiating the first render from subsequent
   * renders.
   *
   * If `adEngine.requestBids` is called directly, as is the case with video ads
   * from the VastUrlPlugin, then renderCycleCount will be undefined as the
   * auction is not part of a render cycle.
   */
  renderCycleCount?: number;
};

export interface Hooks {
  /**
   * Called when the page targeting is initialized.
   * This is the place to modify the page targeting object.
   * @param initialPageTargeting
   */
  onInitPageTargeting?(initialPageTargeting: PageTargeting): void;

  /**
   * Called when the slot config map is initialized.
   * This is the place to modify the slot config map object.
   *
   * If you want to create or remove slots dynamically use onAfterAdNodeManagerInit instead.
   * This hook might be needed for some config modifications before the AdNodeManager is initialized.
   *
   * @param baseSlotsConfig
   */
  onInitSlotConfigMap?(baseSlotsConfig: SlotConfigMap): void;

  /**
   * Called when consent is updated. This is guaranteed to be called at least once when privacy apis
   * have loaded and ad engine continues to setup. If consent is optOut, it can be updated at a
   * later time this hook will be called again with the new consent.
   *
   * Note: Because this is guaranteed to run after privacy apis are available, `consentData.ready` will
   * always be true.
   *
   * This will also provide updates to allowed. Allowed will inform the engine that all ads and
   * anything that would be stopped on optOut must wait until allowed changes to true. This ensures
   * that on pages like kid focused wikis, ads do not render until the user interacts with the
   * age gate modal. This can also be used if certain geo areas required user to interact with the
   * cookie banner before ads are delivered. In any of these cases, the first call will have
   * `allowed.ads` set to false, and if ads are allowed to continue, a follow up call will provide
   * `allowed.ads` set to true.
   */
  onConsentUpdated?(consentData: FandomCmp): void;

  /**
   * Called after the GptWrapper is initialized.
   *
   * At this point window.googletag.pubads() is ready and page targeting has been added to gpt.
   *
   * @param gptWrapper The initialized GptWrapper instance
   */
  onAfterGptWrapperInit?(gptWrapper: GptWrapper): void;

  /**
   * Called after the AdNodeManager is initialized.
   *
   * This is the place to add additional slots or remove some slots (e.g. for A/B testing).
   * @param adNodeManager
   * @param baseSlotsConfig **Deprecated**: Use `adNodeManager.getSlotConfigMap().baseSlots`. This
   * param will be removed in a future release.
   */
  onAfterAdNodeManagerInit?(
    adNodeManager: AdNodeManager,
    baseSlotsConfig: SlotConfigMap["baseSlots"],
  ): void;

  /**
   * Called at the end of the starting of AdEngine, ensuring that all setup of the engine has
   * occurred and consent has been verified. The adNodeManager and gptWrapper refrerences are passed
   * as params to this hook and are also guaranteed to be fully initialized and setup at this point.
   *
   * The following conditions will be true at the time this hook is called:
   *   - GptWrapper will be initialized and services enabled with {@link GptWrapper.enableServices}
   *     resolved.
   *   - Page targeting will be set on GptWrapper using `initialPageTargeting` and this object will
   *     no longer be updated. At this point targeting should be retrieved using
   *     {@link GptWrapper.getAllTargeting} to get up to date targeting values.
   *   - AdNodeManager will be initialized with all slots defined in googletag, including all
   *     repeatable and dynamic slots.`slotConfigMap` should no longer be used at this point.
   *     Instead use {@link AdNode.getSlotConfig} and {@link AdNode.getBiddersConfig} to get the
   *     configurations for a slot.
   *   - onConsentUpdated has been called at least once with `ready = true` and `blocking = false`.
   *     This means for kids wikis and other cases where `blocking` is initially set to true, ad
   *     enging will wait for `blocking` to be set to `false` before continuing with initialization
   *     and calling this hook.
   * @param adNodeManager The fully initialized adNodeManager reference
   * @param gptWrapper The fully initialized gptWrapper reference
   * @param adEngine The AdEngine class that contains `adNodeManager` and `gptWrapper`, along with
   * upcoming functions such as `requestRender(adNodes: AdNode[])` to request slots to bid and
   * render with GAM and `requestBids(adNodes: AdNode[])` to request bids for slots.
   */
  onAdEngineInit?(adEngine: AdEngine): void;

  /**
   * Called when bidders are initalized before the first bids are created. This
   * is where bidders should do any bidder specific initialization before
   * bidding begins.
   *
   * The frist parameter is the {@link AdEngine} instance which has a reference
   * to both the {@link AdNodeManager} and the {@link GptWrapper}. Through both
   * those instances you can get all current information about the state of ad
   * slots, configurations, and targeting.
   *
   * The Full {@link SlotConfigMap} as well as all AdNodes that currently exist
   * on the page are available via `adEngine.adNodeManage` which has the
   * functions {@link AdNodeManager.getSlotConfigMap} and {@link
   * AdNodeManager.getAdNodes}. The `adEngine.gptWrapper` has the {@link
   * GptWrapper} where you can retrieve all the current page targeting via
   * {@link GptWrapper.getAllTargeting}.
   *
   * For slot specific targeting, ad unit paths, and other data, use the
   * functions on {@link AdNode} returned from {@link AdNodeManager.getAdNodes}
   * such as {@link AdNode.getAdUnitPath} or {@link AdNode.getAllTargeting}.
   *
   * ```ts
   * hooks: {
   *   onBiddersInit: (adEngine: AdEngine) => {
   *     const pageTargeting = adEngine.gptWrapper.getAllTargeting()
   *     // all ad nodes _currently_ on the page
   *     const adNodes = adEngine.adNodeManager.getAdNodes()
   *     for (const adNode of adNodes) {
   *       const slotTargeting = adNode.getAllTargeting()
   *       const adUnitPath = adNode.getAdUnitPath()
   *       const slotName = adNode.getSlotName() // ie: incontent_boxad_2
   *       const baseSlotName = adNode.getBaseSlotName() // ie: incontent_boxad
   *        const bidderConfig = adNode.getBiddersConfig(bidder) // "a9" | "prebid"
   *     }
   *   }
   * }
   * ```
   *
   * This hook can be useful for additional operations before the bidders
   * requests are created.  You can extend the bids configuration with some
   * dynamic data like we do in our OpenRTB plugin.  If the data is static then
   * instead of using this plugin update bids configuration.
   *
   * If a Bidder needs to run async code before the first auction starts, then
   * plugins can push a promise to the `initPromises` array. Any promises added
   * to the array will be awaited up to a failsafe timeout, after which the ad
   * engine will continue.
   *
   * @param adEngine Reference to the {@link AdEngine} to get all the information
   * @param initPromises An array of promises that will be awaited after all the
   * calls to `onBiddersInit` are returned. This allows bidders that need to run
   * some async code to push a promise to the array similar to
   * `onCreateBiddersRequests` hook. The promise will only be awaited for a
   * limited time before the ad engine continues with the bidding process.
   */
  onBiddersInit?(adEngine: AdEngine, initPromises: Promise<unknown>[]): void;

  /**
   * Called just before a round of bidding starts.
   * This is the place for some last-minute data transform operations before the ads are requested.
   * Useful if you need enrich or modify A9/Prebid ad unit configs with custom behavior in your plugin. If multiple
   * plugins are using this hook, there is no guarantee of the order in which they will be called.
   *
   * @param bidderSource bidder source (e.g. "a9", "prebid") that is being requested.
   * of the current state of ads on the page.
   * @param auctionData The auction specific information that is passed to all
   * the bidders. This includes the list of AdNodes, auction ID, and the current
   * page targeting object.
   * @param adUnitConfig The BidderAdUnitConfigMap for the bidder source that is being requested.
   */
  onCreateBidderAdUnit?<S extends BidderSource>(
    bidderSource: S,
    auctionData: AuctionData,
    adUnitConfig: BidderAdUnitConfigMap[S],
  ): void;

  /**
   * Called when a round of bidding starts, and plugins using this hook should push a Promise or async function
   * to the `biddersRequest` array passed into the hook that executes bid requests and resolves when
   * complete.
   *
   * All promises will be awaited up to a {@link InstantConfigRepository.icAdEngineOnCreateBiddersRequestsTimeoutMS}
   *
   * The `auctionInfo` parameter contains the auction specific information that
   * is passed to all bidders and contains the list of AdNodes to bid on,
   * auction Id for tracking, and the current page targeting object with latest
   * targeting values.
   *
   * @param biddersRequests Add a promise to this array that resolves when the bid request is
   * complete. Eg: `biddersRequests.push(async () => { ...run bidding code  })`
   * @param auctionInfo The auction specific information that is passed to all
   * the bidders. This includes the list of AdNodes, auction ID, and the current
   * page targeting object.
   * @param adEngine The AdEngine instance.
   * @param onCreateBidderAdUnit Function to emit the `onCreateBidderAdUnit` hook
   * for each bidder ad unit that is created. This will notify all plugins listening
   * and allow them to modify the ad unit configuration before the bid request is sent.
   */
  onCreateBiddersRequests?(
    biddersRequests: Promise<void>[],
    auction: AuctionData,
    adEngine: AdEngine,
    onCreateBidderAdUnit?: Hooks["onCreateBidderAdUnit"],
  ): void;

  /**
   * Called just before GPT requests ads for the first time.
   * This is the place for some last-minute operations before the ads are requested (first call).
   * Useful if you need to listen to GPT events or call GPT methods.
   *
   * @param gpt The GptWrapper instance. @deprecated: use `adEngine.gptWrapper` instead.
   * This param will be removed in a future release.
   * @param adNodeManager The AdNodeManager instance. @deprecated: use `adEngine.adNodeManager`
   * instead. This param will be removed in a future release.
   */
  onBeforeFirstCallAdsRequest?(
    gpt: GptWrapper,
    adNodeManager: AdNodeManager,
  ): void;

  /**
   * Called just before GPT requests ads.
   * It's called for the first call and for each subsequent call (e.g. for refresh).

   * @param adNodes The ad nodes that will be requested.
   */
  onBeforeAdsRequest?(adNodes: AdNode[]): void;
}

export type AdEnginePluginConstructor = new (
  ...args: never[]
) => AdEnginePlugin;

export abstract class AdEnginePlugin<PluginAPI = unknown> {
  #plugins?: Map<AdEnginePluginConstructor, AdEnginePlugin>;
  public api?: PluginAPI = undefined;
  public hooks: Hooks = {};

  static isEnabled(config: InstantConfigRepository): boolean {
    return !!config;
  }

  /**
   * @deprecated
   * Use {@link AdEnginePlugin.setPlugins} that accepts `Map<string, AdEnginePlugin>` instead.
   */
  setPlugins(plugins: AdEnginePlugin[]): void;
  setPlugins(plugins: Map<AdEnginePluginConstructor, AdEnginePlugin>): void;
  setPlugins(
    plugins: Map<AdEnginePluginConstructor, AdEnginePlugin> | AdEnginePlugin[],
  ): void {
    if (Array.isArray(plugins)) {
      this.#plugins = new Map<AdEnginePluginConstructor, AdEnginePlugin>(
        plugins.map((plugin) => [
          plugin.constructor as AdEnginePluginConstructor,
          plugin,
        ]),
      );
    } else {
      this.#plugins = plugins;
    }
  }

  usePlugin<T extends AdEnginePlugin>(
    pluginClass: new (...args: never[]) => T,
  ): T["api"] | undefined {
    return this.#plugins?.get(pluginClass)?.api;
  }
}
